// ─────────────────────────────────────────────────────────────────────────────
// Sprite manifest
//
// Single source of truth for every system sprite sheet shipped with the app
// (served at /sprites/<name>.png, straight from frontend/public/sprites/ — the
// same folder admin-panel uploads land in).
// Add an entry here whenever a new <name>.png is dropped into that folder.
// Admin-uploaded sprites live in the same folder but are not listed here.
// Both the combat canvas renderer and the admin monster-form picker consume
// this list — so a single edit keeps everything in sync.
// ─────────────────────────────────────────────────────────────────────────────

export interface SpriteEntry {
  /** File name without extension — e.g. 'warrior' → /sprites/warrior.png */
  name:  string;
  /** Human-readable label shown in the admin picker */
  label: string;
  /** Emoji shorthand shown next to the label */
  icon:  string;
  /** Identifies if it belongs to a class, a monster or an NPC (talks to players) */
  type: 'class' | 'monster' | 'npc';
}

export const AVAILABLE_SPRITES: SpriteEntry[] = [
  // Classes
  { name: 'warrior', label: 'Warrior', icon: '⚔️', type: 'class' },
  { name: 'mage',    label: 'Mage',    icon: '🔮', type: 'class' },
  { name: 'rogue',   label: 'Rogue',   icon: '🗡️', type: 'class' },
  { name: 'archer',  label: 'Archer',  icon: '🏹', type: 'class' },
  { name: 'cleric',  label: 'Cleric',  icon: '✝️', type: 'class' },
  { name: 'ferreiro', label: 'Ferreiro', icon: '🔨', type: 'class' },

  // Monsters
  { name: 'orc',              label: 'Orc',              icon: '👹', type: 'monster' },
  { name: 'goblin',           label: 'Goblin',           icon: '👺', type: 'monster' },
  { name: 'goblin_scout',     label: 'Goblin Scout',     icon: '👺', type: 'monster' },
  { name: 'orc_warrior',      label: 'Orc Warrior',      icon: '👹', type: 'monster' },
  { name: 'skeleton_archer',  label: 'Skeleton Archer',  icon: '💀', type: 'monster' },
  { name: 'venom_spider',     label: 'Venom Spider',     icon: '🕷️', type: 'monster' },
  { name: 'zombie_brute',     label: 'Zombie Brute',     icon: '🧟', type: 'monster' },
  { name: 'cave_troll',       label: 'Cave Troll',       icon: '🧌', type: 'monster' },
  { name: 'dire_wolf',        label: 'Dire Wolf',        icon: '🐺', type: 'monster' },
  { name: 'vampire_lord',     label: 'Vampire Lord',     icon: '🧛', type: 'monster' },
  { name: 'stone_golem',      label: 'Stone Golem',      icon: '🪨', type: 'monster' },
  { name: 'shadow_dragon',    label: 'Shadow Dragon',    icon: '🐉', type: 'monster' },
];

/**
 * Sprite sheet grid constants — all sheets must share this layout.
 *
 * Grid: 8 rows × 3 columns
 *   Column 0  → idle / standing frame
 *   Column 1  → walk frame A
 *   Column 2  → walk frame B
 *   Rows 0-7  → one row per movement direction (see DIR_* constants in CombatGrid)
 */
export const SPRITE_COLS = 3;
export const SPRITE_ROWS = 8;
